import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslate } from "../i18n";
import useUserProfile from "../hooks/useUserProfile";
import { useCategoryContext } from "../components/CategoryContext";
import { getRoleLabel } from "../i18n/helpers";
import { getImpersonation } from "../utils/session";
import {
  IcoChevron,
  IcoClose2,
  IcoDash,
  IcoLogout,
} from "./layoutIcons";
import SidebarNavItem from "./SidebarNavItem";
import SidebarCategoryNavItem from "./SidebarCategoryNavItem";

export default function Sidebar({
  collapsed,
  setCollapsed,
  onLogout,
  isMobile = false,
  mobileOpen = false,
  onMobileClose,
}) {
  const profile = useUserProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { categories } = useCategoryContext();
  const displayCollapsed = isMobile ? false : collapsed;
  const W = isMobile ? 240 : collapsed ? 64 : 220;

  const imp = getImpersonation();
  const effectiveRole = imp ? imp.userRole : profile.role;
  const isAdmin = effectiveRole === "admin" || effectiveRole === "super-admin";
  const publicCats = categories.filter((c) => c?._id && !c.adminOnly);
  const adminCats = categories.filter((c) => c?._id && c.adminOnly);

  return (
    <nav
      className="glass-sidebar"
      aria-hidden={isMobile && !mobileOpen ? true : undefined}
      inert={isMobile && !mobileOpen ? true : undefined}
      style={
        isMobile
          ? {
              position: "fixed",
              left: 0,
              top: imp ? 37 : 0,
              bottom: 0,
              height: imp ? "calc(100% - 37px)" : "100%",
              width: W,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              zIndex: 200,
              pointerEvents: mobileOpen ? "auto" : "none",
              transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 0.25s cubic-bezier(.4,0,.2,1)",
            }
          : {
              width: W,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              height: imp ? "calc(100vh - 37px)" : "100vh",
              position: "sticky",
              top: imp ? 37 : 0,
              overflow: "hidden",
              transition: "width 0.22s cubic-bezier(.4,0,.2,1)",
            }
      }
    >
      {displayCollapsed && !isMobile ? (
        <div
          style={{
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "1px solid var(--border)",
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setCollapsed(false)}
            className="e-icon-target"
            title={t("layout.sidebar.expandMenu")}
            style={{
              background: "none",
              border: "none",
              color: "var(--muted)",
              padding: 10,
              display: "flex",
              alignItems: "center",
              borderRadius: 8,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--hover-bg)";
              e.currentTarget.style.color = "var(--text)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
              e.currentTarget.style.color = "var(--muted)";
            }}
          >
            <IcoChevron dir="right" s={16} />
          </button>
        </div>
      ) : (
        <div
          style={{
            height: 60,
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "0 18px",
            borderBottom: "1px solid var(--border)",
            flexShrink: 0,
          }}
        >
          <Link
            to="/"
            onClick={onMobileClose}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <img
              src={process.env.PUBLIC_URL + "/assets/svg/eclipse.svg"}
              alt={t("shared.brand.logoAlt")}
              style={{ width: 24, height: 24, flexShrink: 0 }}
            />
            <span
              style={{
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: "0.1em",
                color: "var(--text)",
                whiteSpace: "nowrap",
              }}
            >
              {t("shared.brand.name")}
            </span>
          </Link>
          {isMobile ? (
            <button
              className="e-icon-target"
              onClick={onMobileClose}
              aria-label={t("shared.close")}
              title={t("shared.close")}
              style={{
                marginLeft: "auto",
                background: "none",
                border: "none",
                color: "var(--muted)",
                padding: 8,
                display: "flex",
                alignItems: "center",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              <IcoClose2 />
            </button>
          ) : (
            <button
              onClick={() => setCollapsed(true)}
              className="e-icon-target"
              aria-label={t("layout.sidebar.collapseMenu")}
              title={t("layout.sidebar.collapseMenu")}
              style={{
                marginLeft: "auto",
                background: "none",
                border: "none",
                color: "var(--muted)",
                padding: 4,
                display: "flex",
                alignItems: "center",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              <IcoChevron dir="left" />
            </button>
          )}
        </div>
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          padding: "10px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
        }}
      >
        <SidebarNavItem
          path="/"
          label="layout.sidebar.nav.dashboard"
          Icon={IcoDash}
          iconColor="var(--accent)"
          collapsed={displayCollapsed}
          currentPath={location.pathname}
          onClick={onMobileClose}
        />

        {publicCats.length > 0 && (
          <>
            {!displayCollapsed && (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  color: "var(--muted)",
                  padding: "8px 10px 4px",
                  textTransform: "uppercase",
                }}
              >
                {t("layout.sidebar.section.content")}
              </div>
            )}
            {displayCollapsed && (
              <div
                style={{
                  height: 1,
                  background: "var(--border)",
                  margin: "6px 8px",
                }}
              />
            )}
            {publicCats.map((cat, idx) => (
              <SidebarCategoryNavItem
                key={cat._id}
                cat={cat}
                idx={idx}
                collapsed={displayCollapsed}
                currentPath={location.pathname}
                onClick={onMobileClose}
              />
            ))}
          </>
        )}

        {isAdmin && (
          <>
            {!displayCollapsed && (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.1em",
                  color: "var(--muted)",
                  padding: "12px 10px 4px",
                  textTransform: "uppercase",
                }}
              >
                {t("layout.sidebar.section.admin")}
              </div>
            )}
            {displayCollapsed && (
              <div
                style={{
                  height: 1,
                  background: "var(--border)",
                  margin: "6px 8px",
                }}
              />
            )}
            {adminCats.map((cat, idx) => (
              <SidebarCategoryNavItem
                key={cat._id}
                cat={cat}
                idx={idx}
                collapsed={displayCollapsed}
                currentPath={location.pathname}
                onClick={onMobileClose}
              />
            ))}
          </>
        )}
      </div>

      <div
        style={{
          padding: "10px 8px",
          borderTop: "1px solid var(--border)",
          flexShrink: 0,
        }}
      >
        {!displayCollapsed && (
          <button
            onClick={() => {
              onMobileClose && onMobileClose();
              navigate("/profile");
            }}
            style={{
              width: "100%",
              padding: "8px 12px",
              minHeight: 52,
              marginBottom: 4,
              background:
                location.pathname === "/profile"
                  ? "var(--accent-g)"
                  : "var(--surface)",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              border:
                location.pathname === "/profile"
                  ? "1px solid var(--accent-soft-border)"
                  : "1px solid transparent",
              textAlign: "left",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--hover-bg-md)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background =
                location.pathname === "/profile"
                  ? "var(--accent-g)"
                  : "var(--surface)")
            }
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                background: "var(--accent-g)",
                border: "1.5px solid var(--accent-strong-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--accent)",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {profile.photo ? (
                <img
                  src={`/img/users/${profile.photo}`}
                  alt={t("pages.profile.avatar.alt")}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                (profile.name || profile.email || "U")[0].toUpperCase()
              )}
            </div>
            <div style={{ overflow: "hidden", flex: 1 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--text)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {profile.name || profile.email}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>
                {getRoleLabel(t, profile.role)} · {t("layout.sidebar.profile")}
              </div>
            </div>
            <IcoChevron dir="right" s={12} />
          </button>
        )}

        <button
          onClick={onLogout}
          className="e-touch-target"
          title={displayCollapsed ? t("layout.sidebar.logout") : undefined}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: displayCollapsed ? "10px 14px" : "9px 12px",
            minHeight: 38,
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            width: "100%",
            background: "transparent",
            color: "var(--muted)",
            fontSize: 13,
            fontFamily: "inherit",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--danger-soft-bg)";
            e.currentTarget.style.color = "var(--danger)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--muted)";
          }}
        >
          <IcoLogout s={15} />
          {!displayCollapsed && <span>{t("logout")}</span>}
        </button>
      </div>
    </nav>
  );
}
